import clsx from "clsx"
import { useCallback, useEffect, useMemo, useState } from "react"

import { faIcon } from "@utils/faIcon"

import { Skeleton } from "@particles/index"

import { Icon, Tooltip } from "@atoms"
import { IconSize } from "@atoms/Icon/Icon.types"

import { DEFAULT_ROOT_FONT_SIZE } from "@constants/fonts"

import styles from "./Avatar.module.scss"
import { AvatarProps } from "./Avatar.types"
import AvatarStatus from "./AvatarStatus/AvatarStatus"

const fontSizesBySize: Record<AvatarProps["size"], number> = {
    xxs: 16,
    xs: 20,
    sm: 24,
    md: 28,
    lg: 32,
    xl: 36,
    huge: 80,
}

const iconSizesBySize: Record<AvatarProps["size"], IconSize> = {
    xxs: 8,
    xs: 10,
    sm: 12,
    md: 12,
    lg: 14,
    xl: 16,
    huge: 24,
}

const iconAddonSizesBySize: Record<AvatarProps["size"], IconSize> = {
    xxs: 8,
    xs: 8,
    sm: 10,
    md: 12,
    lg: 12,
    xl: 14,
    huge: 14,
}

// How long a failed avatar URL is remembered before one fresh attempt is allowed.
const FAILED_AVATAR_RETRY_MS = 10 * 60 * 1000

// Avatar URLs that recently failed to load, by failure time. A background-image can't report
// load errors, so a remounted avatar would otherwise re-request a known bad (often gravatar)
// URL on every remount; remembering the failure suppresses that retry storm, while the expiry
// lets an avatar hidden by a transient outage recover without a full reload.
const failedAvatarSources = new Map<string, number>()

function hasRecentlyFailed(src: string): boolean {
    if (!src) {
        return false
    }
    const failedAt = failedAvatarSources.get(src)
    if (failedAt == null) {
        return false
    }
    if (Date.now() - failedAt >= FAILED_AVATAR_RETRY_MS) {
        failedAvatarSources.delete(src)
        return false
    }
    return true
}

// Narrow the props union to the listed variants so a prop only declared on some of them is safe to read.
function hasVariant<Variant extends AvatarProps["variant"]>(
    props: AvatarProps,
    ...variants: Variant[]
): props is Extract<AvatarProps, { variant: Variant }> {
    return (variants as AvatarProps["variant"][]).includes(props.variant)
}

export default function Avatar(props: AvatarProps) {
    const { variant, size, showTooltip = false, baseClassName, iconClassName, isLoading = false } = props

    const src = hasVariant(props, "user", "company") ? (props.src ?? "") : ""
    const fallbackName = hasVariant(props, "user", "user-count", "company") ? (props.fallbackName ?? "") : ""
    const addOnType = hasVariant(props, "user", "object", "company") ? (props.addOnType ?? null) : null
    const userStatus = hasVariant(props, "user") ? (props.addOnStatus ?? null) : null
    const objectStatus = hasVariant(props, "object", "company") ? (props.addOnStatus ?? null) : null
    const addOnIcon = hasVariant(props, "user", "object", "company") ? (props.addOnIcon ?? null) : null
    const addOnIconClassName = hasVariant(props, "user", "object", "company")
        ? (props.addOnIconClassName ?? null)
        : null
    const isOverlapping = hasVariant(props, "user", "user-count") ? (props.isOverlapping ?? false) : false
    const count = hasVariant(props, "user-count") ? props.count : undefined
    const countShowPlus = hasVariant(props, "user-count") ? (props.countShowPlus ?? false) : false
    const isLive = hasVariant(props, "user") ? (props.isLive ?? false) : false
    const liveRingColor = hasVariant(props, "user") ? (props.liveRingColor ?? "transparent") : "transparent"
    const objectIcon = hasVariant(props, "object") ? props.icon : undefined

    // An explicit add-on icon wins; otherwise the icon add-on shows the object status icon in the status color.
    const iconAddon = addOnIcon
        ? { icon: addOnIcon, color: undefined, rotation: undefined }
        : objectStatus?.icon
          ? { icon: objectStatus.icon, color: objectStatus.color, rotation: objectStatus.rotation }
          : null

    const [imageLoadError, setImageLoadError] = useState(() => hasRecentlyFailed(src))

    const handleImageError = useCallback(() => {
        if (src) {
            failedAvatarSources.set(src, Date.now())
        }
        setImageLoadError(true)
    }, [src])

    useEffect(() => {
        setImageLoadError(hasRecentlyFailed(src))
    }, [src])

    const shouldShowInitials = !src || imageLoadError

    const initials = useMemo(() => {
        if (!(variant === "user" || variant === "company")) {
            return null
        }

        const fullNameSplit = fallbackName.split(" ")

        const firstInitial = fullNameSplit.shift()?.charAt(0) || ""
        const lastInitial = fullNameSplit.pop()?.charAt(0) || ""

        const useTwoInitials = variant === "user" && size !== "xxs" && size !== "xs"

        return useTwoInitials ? `${firstInitial}${lastInitial}` : firstInitial
    }, [variant, fallbackName, size])

    const avatarBaseFontSize = useMemo(() => `${fontSizesBySize[size] / DEFAULT_ROOT_FONT_SIZE}rem`, [size]) // Convert the pixel-based size to rem

    if (isLoading) {
        const isCircle = variant === "user" || variant === "user-count" || variant === "user-placeholder"
        const pixelSize = fontSizesBySize[size]

        return (
            <Tooltip
                body={fallbackName}
                isDisabled={!showTooltip || !fallbackName}
                defaultPlacement="bottom"
                wrapChildren={false}
            >
                <div className={clsx(styles.base, styles.loading, styles[variant], styles[`${size}Size`])}>
                    <Skeleton
                        circle={isCircle}
                        width={pixelSize}
                        height={pixelSize}
                        {...(!isCircle && { borderRadius: size === "xxs" || size === "xs" ? 4 : 6 })}
                        enableAnimation={true}
                    />
                </div>
            </Tooltip>
        )
    }

    return (
        <Tooltip
            body={fallbackName}
            isDisabled={!showTooltip || !fallbackName}
            defaultPlacement="bottom"
            wrapChildren={false}
        >
            <div
                className={clsx(styles.base, styles[variant], styles[`${size}Size`], {
                    [styles.overlapping]: isOverlapping,
                    [styles.showingStatus]: addOnType === "statusDot",
                    [styles.showingIconAddon]: addOnType === "icon",
                    [styles.usingMask]: isOverlapping || addOnType === "statusDot" || addOnType === "icon",
                    [styles.live]: isLive,
                })}
                style={
                    {
                        "--live-ring-color": liveRingColor || "transparent",
                        fontSize: avatarBaseFontSize,
                    } as React.CSSProperties
                }
            >
                <div className={styles.container}>
                    <div className={clsx(styles.avatarBackground, baseClassName)}>
                        {(variant === "user" || variant === "company") && initials && shouldShowInitials && (
                            <span className={styles.textContent}>{initials}</span>
                        )}
                        {variant === "user-count" && (
                            <span className={clsx(styles.textContent, styles.numeric)}>
                                {countShowPlus ? "+" : null}
                                {Math.min(count ?? 0, 99)}
                            </span>
                        )}
                        {variant === "user-placeholder" && (
                            <div className={clsx(styles.icon, iconClassName)}>
                                <Icon icon={faIcon({ name: "user", style: "solid" })} size={iconSizesBySize[size]} />
                            </div>
                        )}
                        {(variant === "user" || variant === "company") && src && !imageLoadError && (
                            <>
                                <div
                                    className={clsx(styles.backgroundImage)}
                                    style={{
                                        backgroundImage: `url("${src}")`,
                                    }}
                                    aria-label={`${fallbackName}'s Avatar`}
                                    role="img"
                                />
                                {/* Hidden img to detect load errors (background-image can't detect errors) */}
                                <img src={src} alt="" style={{ display: "none" }} onError={handleImageError} />
                            </>
                        )}
                        {variant === "object" && objectIcon && (
                            <div className={clsx(styles.icon, iconClassName)}>
                                <Icon icon={objectIcon} size={iconSizesBySize[size]} />
                            </div>
                        )}
                    </div>
                </div>

                {addOnType === "statusDot" && userStatus && (
                    <div className={clsx(styles.status, styles.userStatus)}>
                        <AvatarStatus variant="user" status={userStatus} />
                    </div>
                )}
                {addOnType === "statusDot" && objectStatus && (
                    <div className={clsx(styles.status, styles.objectStatus)}>
                        <AvatarStatus variant="object" color={objectStatus.color} />
                    </div>
                )}
                {addOnType === "icon" && iconAddon && (
                    <div className={clsx(styles.iconAddon, addOnIconClassName)} style={{ color: iconAddon.color }}>
                        <Icon icon={iconAddon.icon} rotation={iconAddon.rotation} size={iconAddonSizesBySize[size]} />
                    </div>
                )}
            </div>
        </Tooltip>
    )
}
